import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../Layout/Layout";
import { useSelector, useDispatch } from "react-redux";
import { toggleDialog } from "../../../redux/Slices/Global/globalSlice";
import { resetWizard, setSelectedSmartId } from "../../../redux/Slices/smartAdSchedulerSlice";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "../../../components/ui/Button";
import { Dropdown } from "primereact/dropdown";

import SmartAdScheduleCard from "./Component/SmartAdScheduleCard";
import SmartAdScheduleDialog from "./Component/SmartAdScheduleDialog";
import SmartAdLogsPanel from "./Component/SmartAdLogsPanel";
import FeatureLocked from "../../SharedComponents/FeatureLocked";
import { useGetSmartAdSchedules, useDeleteSmartAdSchedule } from "../../../queries/smartAdScheduler";

import "../css/apps.css";

const buildStatusOpts = (t) => [
    { label: t("common.all"),        value: null        },
    { label: t("status.active"),     value: "active"    },
    { label: t("status.completed"),  value: "completed" },
    { label: t("status.cancelled"),  value: "cancelled" },
];

export default function SmartAdSchedulerIndex() {
    const { t }      = useTranslation();
    const dispatch   = useDispatch();
    const openDialog = useSelector((s) => s.global.openDialog);

    const [statusFilter, setStatusFilter] = useState(null);

    const STATUS_OPTS = buildStatusOpts(t);

    const params = useMemo(() =>
        Object.fromEntries(Object.entries({ status: statusFilter }).filter(([, v]) => v)),
    [statusFilter]);

    const { data, isLoading, error } = useGetSmartAdSchedules({ ...params, refetchInterval: 30_000 });
    const deleteMutation = useDeleteSmartAdSchedule();

    const schedules = Array.isArray(data) ? data : data?.data ?? [];

    const subNavbarProps = useMemo(() => ({
        icon: <i className="fa-solid fa-robot text-secondary fs-2" />,
    }), []);

    const subNavbarContent = useMemo(() => (
        <div className="flex justify-end w-100">
            <button
                className="btn btn-primary mx-1"
                disabled={isLoading}
                onClick={() => {
                    dispatch(resetWizard());
                    dispatch(toggleDialog("SmartAdScheduleDialog"));
                }}
            >
                {t("smart_ads.new_schedule")}
            </button>
        </div>
    ), [isLoading, dispatch]);

    if (error?.response?.status === 402) {
        return (
            <Layout>
                <FeatureLocked feature="smart_ad_scheduler" message={error.response.data?.message} />
            </Layout>
        );
    }

    return (
        <Layout subNavbarProps={subNavbarProps} subNavbarContent={subNavbarContent}>
            <div className="card">
                <div className="d-flex flex-column m-4">

                    {/* Filter bar */}
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <span className="text-sm text-gray-600 font-medium">{t("smart_ads.filter_by_status")}</span>
                        <Dropdown
                            value={statusFilter}
                            options={STATUS_OPTS}
                            onChange={(e) => setStatusFilter(e.value)}
                            className="text-sm"
                        />
                        {!isLoading && (
                            <span className="text-xs text-gray-400 ml-auto">
                                {t("smart_ads.n_schedules", { count: schedules.length })}
                            </span>
                        )}
                    </div>

                    {/* Content */}
                    {isLoading ? (
                        <div className="flex justify-center py-16">
                            <ProgressSpinner />
                        </div>
                    ) : schedules.length === 0 ? (
                        <div className="text-center py-16 text-gray-400">
                            <i className="fa-solid fa-robot text-4xl mb-3 block" />
                            <p className="text-sm">{t("smart_ads.no_schedules")}</p>
                            <Button
                                label={t("smart_ads.create_first")}
                                className="p-button-text p-button-sm mt-2"
                                onClick={() => {
                                    dispatch(resetWizard());
                                    dispatch(toggleDialog("SmartAdScheduleDialog"));
                                }}
                            />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {schedules.map((schedule) => (
                                <SmartAdScheduleCard
                                    key={schedule.id}
                                    schedule={schedule}
                                    onDelete={(id) => deleteMutation.mutate(id)}
                                    onViewLogs={(id) => dispatch(setSelectedSmartId(id))}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {openDialog?.SmartAdScheduleDialog && <SmartAdScheduleDialog />}
            <SmartAdLogsPanel />
        </Layout>
    );
}
